import React, { useEffect, useState } from "react";
import "./Customer.css";
import { Col, Grid, Popover, Tag, Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import CustomerHeader from "./CustomerHeader";
import CustomTableWithPagination from "../../components/CustomTableWithPagination/CustomTableWithPagination";
import { useLazyGetMobCreditCustomersQuery } from "../../apis/customer";
import { dateFormat } from "../../commonFuntions/CommonUtilFunctions";
import TableSkeleton from "../../components/skeleton/TableSkeleton";
import NoDataFound from "../../components/EmptyTable/NoDataFound";

const { Text } = Typography;

const MobCreditCustomers = () => {
  const screens = Grid.useBreakpoint();
  const isMobileViewport = !screens.md;
  const [searchedValue, setSearchedValue] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const [
    getMobCreditCustomersApi,
    {
      data: tableData,
      isFetching,
    },
  ] = useLazyGetMobCreditCustomersQuery();

  useEffect(() => {
    getMobCreditCustomersApi();
  }, [getMobCreditCustomersApi]);

  const handleSearchedValue = (params) => {
    setSearchedValue(params);
    setCurrentPage(1);
  };

  const handlePageChange = async (currPage) => {
    const params = {
      page: currPage,
      ...(searchedValue?.search ? { search: searchedValue.search } : {}),
    };
    setCurrentPage(currPage);
    await getMobCreditCustomersApi(params);
  };

  const getStatusTagColor = (status) => {
    const normalized = String(status || "").toUpperCase();
    if (normalized === "ACTIVE") return "success";
    if (normalized === "INACTIVE") return "default";
    if (normalized === "SUSPENDED") return "warning";
    if (normalized === "BLOCKED") return "error";
    return "default";
  };

  const getMobCreditUIStatus = (primaryStatus, accountStatus) => {
    const primary = String(primaryStatus || "").toUpperCase();
    const secondary = String(accountStatus || "").toUpperCase();
    if (primary === "ACTIVE" && secondary === "ACTIVE") return { label: "ACTIVE", color: "success" };
    if (primary === "ACTIVE" && secondary === "INACTIVE") return { label: "INACTIVE", color: "orange" };
    if (primary === "REJECTED") return { label: "REJECTED", color: "error" };
    if (primary === "PRE_APPROVED" || primary === "INCOMPLETE" || primary === "UNDER_REVIEW") {
      return { label: "IN PROGRESS", color: "gold" };
    }
    return { label: primaryStatus || "-", color: "default" };
  };

  const columns = [
    {
      title: "Business Name",
      dataIndex: "business_name",
      key: "business_name",
      fixed: isMobileViewport ? undefined : "left",
      width: "250px",
      render: (params) => <Text>{params || "-"}</Text>,
    },
    {
      title: "Business mobile",
      dataIndex: "phone_number",
      key: "phone_number",
      width: "200px",
      render: (params) => <Text>{params || "-"}</Text>,
    },
    {
      title: "Business Segment",
      dataIndex: "business_segment",
      key: "business_segment",
      width: "200px",
      render: (value) => {
        if (Array.isArray(value)) return <Text>{value.join(", ") || "-"}</Text>;
        return <Text>{value || "-"}</Text>;
      },
    },
    {
      title: "mobCREDIT Status",
      dataIndex: "rupifi",
      key: "rupifi",
      width: "220px",
      render: (rupifi) => {
        const { label: mobCreditLabel, color: mobCreditColor } = getMobCreditUIStatus(
          rupifi?.primary_status,
          rupifi?.account_status
        );
        const popoverContent = (
          <div className="business-segment-popover-content">
            <div className="business-segment-popover-row">
              <span className="business-segment-popover-label">Primary Status:</span>{" "}
              <Tag color={getStatusTagColor(rupifi?.primary_status)}>
                {rupifi?.primary_status || "-"}
              </Tag>
            </div>
            <div className="business-segment-popover-row">
              <span className="business-segment-popover-label">Account Status:</span>{" "}
              <Tag color={getStatusTagColor(rupifi?.account_status)}>
                {rupifi?.account_status || "-"}
              </Tag>
            </div>
            <div className="business-segment-popover-row">
              <span className="business-segment-popover-label">Current Limit:</span>{" "}
              <span>
                {rupifi?.current_limit !== undefined && rupifi?.current_limit !== null
                  ? `₹ ${rupifi.current_limit}`
                  : "-"}
              </span>
            </div>
            <div className="business-segment-popover-row">
              <span className="business-segment-popover-label">Balance:</span>{" "}
              <span>
                {rupifi?.balance !== undefined && rupifi?.balance !== null
                  ? `₹ ${rupifi.balance}`
                  : "-"}
              </span>
            </div>
          </div>
        );
        return (
          <span className="business-segment-cell">
            <Tag color={mobCreditColor}>{mobCreditLabel}</Tag>
            <Popover
              content={popoverContent}
              trigger="click"
              placement="bottomLeft"
              overlayClassName="business-segment-popover"
            >
              <InfoCircleOutlined style={{ color: "#8c8c8c", cursor: "pointer" }} />
            </Popover>
          </span>
        );
      },
    },
    {
      title: "Registered date",
      dataIndex: "created_at",
      key: "created_at",
      width: "200px",
      render: (params) => (
        <Col>
          <Text>{params ? dateFormat(params) : "-"}</Text>
        </Col>
      ),
    },
    {
      title: "GSTIN",
      dataIndex: "gst",
      key: "gst",
      width: "200px",
      render: (params) => <Text>{params || "-"}</Text>,
    },
  ];

  return (
    <Col className="customer-container">
      <Text className="customer_title">mobCREDIT Customers</Text>
      <CustomerHeader
        getAllCustomersApi={getMobCreditCustomersApi}
        isFetching={isFetching}
        handleSearchedValue={handleSearchedValue}
        showAddCustomer={false}
        showExport={false}
      />
      <CustomTableWithPagination
        className="order-list-table"
        tableDataSource={tableData?.data?.results}
        tableColumns={columns}
        locale={{
          emptyText: isFetching ? (
            <TableSkeleton length={10} />
          ) : (
            <NoDataFound text={"No mobCREDIT customers found!"} />
          ),
        }}
        isLoading={tableData?.data?.results?.length && isFetching}
        scroll={{ x: isMobileViewport ? 1100 : "100%" }}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        itemsPerPage={30}
        totalEntries={!isFetching && tableData?.data?.pagination?.total_entries}
      />
    </Col>
  );
};

export default MobCreditCustomers;
